// Socket-Anbindung des Pakets „Extras". Die Shell besitzt die Socket-Verbindung und ruft nach dem Verbinden
// `bindExtrasRealtime(socket, queryClient)` auf (Rückgabe: Abmeldefunktion). Ohne diese Anbindung funktionieren
// die Seiten trotzdem – sie fragen dann per Polling nach (Exporte, Brücke, Erinnerungen).
import type { ServerToClientEvents } from '@lagerfeuer/shared';
import type { QueryClient } from '@tanstack/react-query';
import type { LfSocket } from '../../lib/socket';
import { applyBridgeStatus, applyLimits } from './admin/realtime';
import { applyExportUpdate } from './exports/realtime';
import { handleReminderNotification } from './reminders/realtime';
import { applyScheduledUpdate } from './scheduled/realtime';
import { playSoundboardEvent } from './soundboard-utils';
import { incomingKey } from './webhooks/webhooks-utils';

/** Query-Key der Soundliste eines Servers – muss mit soundboard.tsx übereinstimmen (`['extras', 'sounds', serverId]`). */
export const soundsKey = (serverId: string) => ['extras', 'sounds', serverId] as const;

export function bindExtrasRealtime(socket: LfSocket, queryClient: QueryClient): () => void {
  const handlers: Partial<ServerToClientEvents> = {
    EXPORT_UPDATE: (p) => applyExportUpdate(queryClient, p),
    SCHEDULED_MESSAGE_UPDATE: (p) => applyScheduledUpdate(queryClient, p),
    NOTIFICATION_CREATE: (p) => handleReminderNotification(queryClient, p),
    BRIDGE_STATUS_UPDATE: (p) => applyBridgeStatus(queryClient, p),
    PLATFORM_LIMITS_UPDATE: (p) => applyLimits(queryClient, p),
    WEBHOOKS_UPDATE: (p) => void queryClient.invalidateQueries({ queryKey: incomingKey(p.serverId) }),
    SOUNDS_UPDATE: (p) => void queryClient.invalidateQueries({ queryKey: soundsKey(p.serverId) }),
    SOUNDBOARD_PLAY: (p) => {
      playSoundboardEvent(p);
    },
  };
  const entries = Object.entries(handlers) as [keyof ServerToClientEvents, (...args: never[]) => void][];
  for (const [event, fn] of entries) socket.on(event, fn as never);
  return () => {
    for (const [event, fn] of entries) socket.off(event, fn as never);
  };
}
